import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Save, Eye, EyeOff, FileText, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ConfirmDialog from '@/components/ConfirmDialog';

const DOCS = {
  terms: {
    label: 'Terms of Service',
    icon: FileText,
    fetchPath: '/terms',
    savePath: '/admin/terms',
    publicPath: '/terms',
    saveWarning: true,
  },
  privacy: {
    label: 'Privacy Policy',
    icon: ShieldCheck,
    fetchPath: '/privacy',
    savePath: '/admin/privacy',
    publicPath: '/privacy',
    saveWarning: false,
  },
};

export default function AdminTerms() {
  const [activeTab, setActiveTab] = useState('terms');
  const [content, setContent] = useState({ terms: '', privacy: '' });
  const [lastUpdated, setLastUpdated] = useState({ terms: '', privacy: '' });
  const [dirty, setDirty] = useState({ terms: false, privacy: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [confirmTermsSave, setConfirmTermsSave] = useState(false);

  const doc = DOCS[activeTab];
  const text = content[activeTab];

  useEffect(() => {
    const load = async () => {
      try {
        const [terms, privacy] = await Promise.all([
          api.get(DOCS.terms.fetchPath),
          api.get(DOCS.privacy.fetchPath),
        ]);
        setContent({ terms: terms.data.content || '', privacy: privacy.data.content || '' });
        setLastUpdated({ terms: terms.data.last_updated || '', privacy: privacy.data.last_updated || '' });
      } catch (error) {
        logger.error('Error fetching legal documents:', error);
        toast.error('Failed to load legal documents');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const doSave = async () => {
    setSaving(true);
    try {
      await api.put(doc.savePath, { content: text });
      toast.success(`${doc.label} published successfully`);
      setDirty((d) => ({ ...d, [activeTab]: false }));
      setLastUpdated((l) => ({ ...l, [activeTab]: new Date().toISOString() }));
    } catch (error) {
      logger.error(`Error saving ${doc.label}:`, error);
      toast.error(`Failed to save ${doc.label}`);
    } finally {
      setSaving(false);
      setConfirmTermsSave(false);
    }
  };

  const handleSaveClick = () => {
    if (doc.saveWarning) {
      setConfirmTermsSave(true);
    } else {
      doSave();
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

  return (
    <div className="p-6" data-testid="admin-legal-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
            Legal Documents
          </h1>
          <p className="text-sm mt-1" style={{ color: '#73766A' }}>
            {lastUpdated[activeTab] && (
              <>Last published: {new Date(lastUpdated[activeTab]).toLocaleString()}</>
            )}
            {dirty[activeTab] && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: '#FFF4E5', color: '#9E6752' }}>
                Unsaved changes
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowPreview((p) => !p)}
            data-testid="legal-preview-toggle"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={saving || !dirty[activeTab]}
            data-testid="legal-save-button"
            style={{ background: '#9E6752', color: 'white' }}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Publishing...' : `Publish ${doc.label}`}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {Object.entries(DOCS).map(([key, d]) => {
          const Icon = d.icon;
          const active = key === activeTab;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`legal-tab-${key}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              style={active
                ? { background: '#20212B', color: '#FED7A5' }
                : { background: '#F3EDE5', color: '#73766A' }}
            >
              <Icon className="w-4 h-4" />
              {d.label}
              {dirty[key] && <span className="w-2 h-2 rounded-full" style={{ background: '#9E6752' }} />}
            </button>
          );
        })}
      </div>

      {/* Terms re-acceptance warning */}
      {activeTab === 'terms' && (
        <div className="flex items-start gap-3 rounded-lg p-3 mb-4 text-sm" style={{ background: '#FFF4E5', border: '1px solid #FED7A5' }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#9E6752' }} />
          <p style={{ color: '#73766A' }}>
            Publishing the Terms of Service will require <strong style={{ color: '#9E6752' }}>every user to re-accept</strong> on their next visit.
          </p>
        </div>
      )}

      {/* Editor + Preview */}
      <div className={`grid gap-4 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        <div>
          <Textarea
            value={text}
            onChange={(e) => {
              const v = e.target.value;
              setContent((c) => ({ ...c, [activeTab]: v }));
              setDirty((d) => ({ ...d, [activeTab]: true }));
            }}
            data-testid="legal-content-editor"
            className="font-mono text-sm leading-relaxed min-h-[65vh] resize-y"
            placeholder={`Write the ${doc.label} here...`}
          />
          <p className="text-xs mt-2 text-right" style={{ color: '#73766A' }}>
            {wordCount.toLocaleString()} words · {text.length.toLocaleString()} characters
          </p>
        </div>

        {showPreview && (
          <div
            data-testid="legal-preview-pane"
            className="rounded-xl border bg-white p-6 min-h-[65vh] max-h-[75vh] overflow-y-auto"
          >
            <p className="text-xs uppercase tracking-wider mb-4 font-semibold" style={{ color: '#9E6752' }}>
              Preview · as shown at {doc.publicPath}
            </p>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: '#534145', fontSize: '0.9rem', lineHeight: 1.7 }}>
              {text}
            </pre>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmTermsSave}
        onOpenChange={setConfirmTermsSave}
        title="Publish new Terms of Service?"
        description="Every user will be required to re-accept the Terms the next time they use the site. This cannot be undone."
        confirmText="Publish & Require Re-acceptance"
        onConfirm={doSave}
      />
    </div>
  );
}
